/**
 * Markup + takeoff renderer: a 1:1 port of Centerline for Mac's
 * TakeoffDrawingAnnotation, ShapeMarkupRenderer and custom-stamp layout.
 * Page space, y up (see ctx.ts). Same proportions, line widths, dashes,
 * fills, label plates and rotation as the Mac, so a plan looks the same in
 * the browser as on the desktop.
 */
import type { PageScale } from "../types.js";
import type {
  ApprovalStampDetails, CloudStyle, CustomStampLayout, HatchStyle, HighlightStyle, Markup, Measurement, MeasurementLabelStyle,
  QuickMeasureStyle, ShapeMarkupStyle, SignatureAppearance, TextBoxDetails, ToolChestImageAsset, Vec2, AppearanceBorderStyle,
} from "../markup/types.js";
import { effectiveLabelStyle, effectiveMeasurementColor, labelFontSize, labelVisible, onPlanLabelText, quickMeasureMarkupLabel, quickMeasureVertices } from "../markup/quantities.js";
import { css, cssFont, hexRGBA, palette, paletteRGBA, palettePdfRGBA, SYSTEM, withAlpha, type RGBA } from "./colors.js";
import { type Ctx, type Rect, type RenderEnv, fromBox, inset, maxX, maxY, midX, midY, rect } from "./ctx.js";
import { drawMOT } from "./mot.js";
import { ASCENT, DESCENT, drawLabel, fittedFontSize, textAt, wrapLines } from "./text.js";

const deg = (d: number) => (d * Math.PI) / 180;
export function rotateAbout(ctx: Ctx, cx: number, cy: number, degrees: number) {
  ctx.translate(cx, cy); ctx.rotate(deg(degrees)); ctx.translate(-cx, -cy);
}
function path(ctx: Ctx, pts: readonly Vec2[], close: boolean) {
  ctx.beginPath();
  pts.forEach(([x, y], i) => (i ? ctx.lineTo(x, y) : ctx.moveTo(x, y)));
  if (close) ctx.closePath();
}
function fillEllipse(ctx: Ctx, cx: number, cy: number, r: number) { ctx.beginPath(); ctx.ellipse(cx, cy, r, r, 0, 0, Math.PI * 2); ctx.fill(); }
function strokeR(ctx: Ctx, r: Rect) { ctx.strokeRect(r.x, r.y, r.w, r.h); }
function fillR(ctx: Ctx, r: Rect) { ctx.fillRect(r.x, r.y, r.w, r.h); }

// MARK: - Shared label geometry (TakeoffDrawingAnnotation statics)

export function midpointAlongPath(points: readonly Vec2[]): Vec2 | null {
  if (!points.length) return null;
  if (points.length < 2) return points[0]!;
  let total = 0;
  for (let i = 1; i < points.length; i++) total += Math.hypot(points[i]![0] - points[i - 1]![0], points[i]![1] - points[i - 1]![1]);
  if (!(total > 0)) return points[0]!;
  let travelled = 0;
  for (let i = 1; i < points.length; i++) {
    const a = points[i - 1]!, b = points[i]!;
    const seg = Math.hypot(b[0] - a[0], b[1] - a[1]);
    if (travelled + seg >= total / 2) {
      const t = seg > 0 ? (total / 2 - travelled) / seg : 0;
      return [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t];
    }
    travelled += seg;
  }
  return points[Math.floor(points.length / 2)]!;
}

export function polygonContains(p: Vec2, pts: readonly Vec2[]): boolean {
  let inside = false;
  for (let i = 0; i < pts.length; i++) {
    const a = pts[i]!, b = pts[(i + 1) % pts.length]!;
    if (a[1] > p[1] !== b[1] > p[1]) {
      const xAtY = ((b[0] - a[0]) * (p[1] - a[1])) / (b[1] - a[1]) + a[0];
      if (p[0] < xAtY) inside = !inside;
    }
  }
  return inside;
}

export function polygonInteriorPoint(pts: readonly Vec2[]): Vec2 | null {
  if (pts.length < 3) return null;
  let a2 = 0, cx = 0, cy = 0;
  for (let i = 0; i < pts.length; i++) {
    const a = pts[i]!, b = pts[(i + 1) % pts.length]!;
    const cross = a[0] * b[1] - b[0] * a[1];
    a2 += cross; cx += (a[0] + b[0]) * cross; cy += (a[1] + b[1]) * cross;
  }
  if (Math.abs(a2) > 0.001) {
    const c: Vec2 = [cx / (3 * a2), cy / (3 * a2)];
    if (polygonContains(c, pts)) return c;
  }
  const b = pointsBounds(pts);
  const center: Vec2 = [midX(b), midY(b)];
  if (polygonContains(center, pts)) return center;
  for (let y = 1; y <= 5; y++) for (let x = 1; x <= 5; x++) {
    const c: Vec2 = [b.x + (b.w * x) / 6, b.y + (b.h * y) / 6];
    if (polygonContains(c, pts)) return c;
  }
  return pts[0]!;
}

export function pointsBounds(pts: readonly Vec2[]): Rect {
  if (!pts.length) return rect(0, 0, 1, 1);
  let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
  for (const [x, y] of pts) { x0 = Math.min(x0, x); y0 = Math.min(y0, y); x1 = Math.max(x1, x); y1 = Math.max(y1, y); }
  return rect(x0, y0, x1 - x0, y1 - y0);
}

export function labelAnchor(points: readonly Vec2[], closed: boolean): Vec2 | null {
  if (!points.length) return null;
  return closed ? polygonInteriorPoint(points) : midpointAlongPath(points);
}

/** Plate size scaled from the text size (character count × 0.62 em, like the Mac). */
export function labelSize(text: string, fontSize: number): { w: number; h: number } {
  const lines = text.split("\n");
  const longest = Math.max(0, ...lines.map((l) => [...l].length));
  return { w: Math.max(fontSize * 5, longest * fontSize * 0.62 + fontSize * 2.2), h: lines.length * fontSize * 1.3 + fontSize * 1.2 };
}

export function measurementLabelRect(text: string, points: readonly Vec2[], closed: boolean, style: MeasurementLabelStyle): Rect | null {
  const a = labelAnchor(points, closed);
  if (!a) return null;
  const size = labelSize(text, labelFontSize(style));
  const [ox, oy] = style.offset ?? [0, 0];
  return rect(a[0] + ox - size.w / 2, a[1] + oy - size.h / 2, size.w, size.h);
}

// MARK: - Takeoffs

function drawVertex(ctx: Ctx, [x, y]: Vec2, color: string) {
  const arm = 4.2;
  ctx.lineCap = "butt"; ctx.lineWidth = 1; ctx.strokeStyle = color;
  ctx.beginPath(); ctx.moveTo(x - arm, y); ctx.lineTo(x + arm, y); ctx.moveTo(x, y - arm); ctx.lineTo(x, y + arm); ctx.stroke();
  ctx.fillStyle = color; fillEllipse(ctx, x, y, 1.15);
}

function drawArrowhead(ctx: Ctx, s: Vec2, e: Vec2, color: string) {
  const dx = e[0] - s[0], dy = e[1] - s[1], d = Math.hypot(dx, dy);
  if (!(d > 0.01)) return;
  const ux = dx / d, uy = dy / d, len = Math.min(Math.max(9, d * 0.24), 18), hw = len * 0.42;
  const bx = e[0] - ux * len, by = e[1] - uy * len;
  ctx.fillStyle = color;
  path(ctx, [e, [bx - uy * hw, by + ux * hw], [bx + uy * hw, by - ux * hw]], true);
  ctx.fill();
}

function drawMeasurementLabel(ctx: Ctx, text: string, points: readonly Vec2[], closed: boolean, style: MeasurementLabelStyle) {
  if (!labelVisible(style)) return;
  const r = measurementLabelRect(text, points, closed, style);
  if (!r) return;
  ctx.save();
  if (style.rotationDegrees) rotateAbout(ctx, midX(r), midY(r), style.rotationDegrees);
  const text_ = paletteRGBA(style.textColor);
  if (style.showBackground) {
    ctx.fillStyle = style.backgroundColor === "clear" ? withAlpha(SYSTEM.white!, 0.88) : withAlpha(paletteRGBA(style.backgroundColor), 0.9);
    fillR(ctx, r);
    ctx.strokeStyle = withAlpha(text_, 0.55 * text_.a); ctx.lineWidth = 0.8; strokeR(ctx, r);
  }
  const fs = labelFontSize(style), pad = Math.max(2, fs * 0.5);
  drawLabel(ctx, text, inset(r, pad, pad * 0.8), css(text_), { font: "helveticaBold", fontSize: fs });
  ctx.restore();
}

/** One takeoff row (linear / arrow / area with cutouts / count). */
export function drawMeasurement(ctx: Ctx, m: Measurement, scale: PageScale | null | undefined): void {
  if (!m.points.length) return; // AI rows live only in the table
  const rgba = paletteRGBA(effectiveMeasurementColor(m)), color = css(rgba);
  ctx.save();
  ctx.lineJoin = "round"; ctx.lineCap = "round";
  if (m.kind === "count") { for (const p of m.points) drawVertex(ctx, p, color); ctx.restore(); return; }
  const closed = m.kind === "area";
  const holes = closed ? (m.cutouts ?? []) : [];
  ctx.lineCap = "butt";
  path(ctx, m.points, closed);
  for (const hole of holes) if (hole.length >= 3) { hole.forEach(([x, y], i) => (i ? ctx.lineTo(x, y) : ctx.moveTo(x, y))); ctx.closePath(); }
  ctx.lineWidth = 1.7; ctx.strokeStyle = color;
  if (closed) { ctx.fillStyle = withAlpha(rgba, 0.14); ctx.fill("evenodd"); }
  ctx.stroke();
  if (holes.length) {
    ctx.save();
    ctx.strokeStyle = css(SYSTEM.red!); ctx.lineWidth = 1.2; ctx.setLineDash([5, 3]);
    for (const hole of holes) if (hole.length >= 3) { path(ctx, hole, true); ctx.stroke(); }
    ctx.restore();
  }
  for (const p of m.points) drawVertex(ctx, p, color);
  if (m.kind === "arrow" && m.points.length >= 2) drawArrowhead(ctx, m.points[m.points.length - 2]!, m.points[m.points.length - 1]!, color);
  const label = onPlanLabelText(m, scale);
  if (label) drawMeasurementLabel(ctx, label, m.points, closed, effectiveLabelStyle(m));
  ctx.restore();
}

// MARK: - Markups

function dashFor(style: AppearanceBorderStyle, width: number): number[] | null {
  switch (style) {
    case "solid": return null;
    case "dashed": return [Math.max(4, width * 3), Math.max(2, width * 1.5)];
    case "dotted": return [0.01, Math.max(2, width * 2)];
  }
}

function aspectFit(img: { width: number; height: number }, r: Rect): Rect {
  const a = img.width / img.height, ra = r.w / r.h;
  return a > ra ? rect(r.x, midY(r) - r.w / a / 2, r.w, r.w / a) : rect(midX(r) - (r.h * a) / 2, r.y, r.h * a, r.h);
}
/** Images are stored top-down, page space is y-up: flip locally. */
function drawImageIn(ctx: Ctx, img: { image: unknown; width: number; height: number }, target: Rect) {
  ctx.save();
  ctx.translate(target.x, maxY(target));
  ctx.scale(1, -1);
  ctx.drawImage(img.image, 0, 0, target.w, target.h);
  ctx.restore();
}

function textFont(d: TextBoxDetails): string {
  return cssFont(d.style.font, Math.max(1, d.style.fontSize), { bold: d.isBold, italic: d.isItalic ?? false });
}
function textColor(d: TextBoxDetails): string {
  const hex = hexRGBA(d.textHex);
  return css(hex ?? palettePdfRGBA(d.style.textColor));
}
function textFill(d: TextBoxDetails): string | null {
  const hex = hexRGBA(d.fillHex);
  if (hex) return css(hex);
  return d.style.fillColor === "clear" ? null : palette(d.style.fillColor);
}

export const TEXT_BOX_INSET = 5;

function drawTextBox(ctx: Ctx, d: TextBoxDetails, r: Rect) {
  const s = d.style;
  if (d.editRole === "redaction") {
    ctx.fillStyle = withAlpha(SYSTEM.red!, 0.16); fillR(ctx, r);
    ctx.strokeStyle = css(SYSTEM.red!); ctx.lineWidth = 1.5; strokeR(ctx, r);
    ctx.lineWidth = 0.6;
    ctx.beginPath(); ctx.moveTo(r.x, r.y); ctx.lineTo(maxX(r), maxY(r)); ctx.moveTo(r.x, maxY(r)); ctx.lineTo(maxX(r), r.y); ctx.stroke();
    return;
  }
  const fill = textFill(d);
  if (fill) { ctx.fillStyle = fill; fillR(ctx, r); }
  if (!(d.isTypewriter ?? false) && !d.editRole && s.borderColor !== "clear" && s.borderWidth > 0) {
    ctx.strokeStyle = palette(s.borderColor); ctx.lineWidth = Math.max(0.5, s.borderWidth);
    if (s.borderStyle === "dashed") ctx.setLineDash([4, 2]);
    strokeR(ctx, r); ctx.setLineDash([]);
  }
  const pad = d.editRole ? 0 : TEXT_BOX_INSET;
  const inner = inset(r, pad, pad);
  if (!d.text || inner.w <= 1 || inner.h <= 1) return;
  const size = Math.max(1, s.fontSize);
  ctx.font = textFont(d);
  ctx.fillStyle = textColor(d);
  const lineHeight = size * (ASCENT + DESCENT);
  const x = (w: number) => (d.alignment === "left" ? inner.x : d.alignment === "right" ? maxX(inner) - w : midX(inner) - w / 2);
  if (d.editRole === "replacement") {
    // Last line on the box's baseline, earlier lines stacked above it.
    const lines = d.text.split("\n");
    lines.forEach((line, i) => {
      if (!line) return;
      const w = ctx.measureText(line).width;
      const xx = d.alignment === "left" ? inner.x + 0.75 : d.alignment === "right" ? maxX(inner) - 0.75 - w : midX(inner) - w / 2;
      textAt(ctx, line, xx, inner.y + 0.75 + size * DESCENT + (lines.length - 1 - i) * lineHeight);
    });
    return;
  }
  let baseline = maxY(inner) - size * ASCENT;
  for (const line of wrapLines(ctx, d.text, inner.w)) {
    if (baseline - size * DESCENT < inner.y - 0.5) break; // Core Text drops lines that don't fit the frame
    if (line) textAt(ctx, line, x(ctx.measureText(line).width), baseline);
    baseline -= lineHeight;
  }
}

/** DateFormatter .medium date + .short time (en_US): "Oct 9, 2026 at 2:47 PM". */
export function defaultTimestamp(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const date = d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  const time = d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
  return `${date} at ${time}`;
}

function denorm([x, y]: Vec2, r: Rect): [number, number] { return [r.x + x * r.w, r.y + y * r.h]; }

function drawSignature(ctx: Ctx, a: SignatureAppearance, r: Rect, env: RenderEnv) {
  const s = a.style;
  if (a.inkOnly === true) {
    if (s.fillColor !== "clear") { ctx.fillStyle = palette(s.fillColor); fillR(ctx, r); }
    if (s.borderColor !== "clear" && s.borderWidth > 0) {
      ctx.strokeStyle = palette(s.borderColor); ctx.lineWidth = s.borderWidth;
      if (s.borderStyle === "dashed") ctx.setLineDash([4, 2]);
      strokeR(ctx, r); ctx.setLineDash([]);
    }
    const caption = (a.inkCaption ?? "").trim();
    const capH = caption ? r.h * 0.2 : 0;
    const ink = inset(rect(r.x, r.y + capH, r.w, r.h - capH), r.w * 0.02, (r.h - capH) * 0.05);
    if (ink.w > 0.5 && ink.h > 0.5) {
      const img = a.inkImageData ? env.image?.(a.inkImageData) ?? null : null;
      if (img) drawImageIn(ctx, img, aspectFit(img, ink));
      else {
        const aspect = Math.max(0.05, a.inkAspectRatio ?? ink.w / ink.h);
        const t = ink.w / ink.h > aspect ? rect(midX(ink) - (ink.h * aspect) / 2, ink.y, ink.h * aspect, ink.h) : rect(ink.x, midY(ink) - ink.w / aspect / 2, ink.w, ink.w / aspect);
        ctx.strokeStyle = palette(s.textColor);
        ctx.lineWidth = Math.max(0.6, Math.min(t.h * 0.04, t.w * 0.012 + 0.6));
        ctx.lineCap = "round"; ctx.lineJoin = "round";
        for (const p of a.freehandPaths) {
          if (p.points.length < 2) continue;
          ctx.beginPath();
          p.points.forEach((q, i) => { const [x, y] = denorm(q, t); if (i) ctx.lineTo(x, y); else ctx.moveTo(x, y); });
          ctx.stroke();
        }
      }
    }
    if (caption) drawLabel(ctx, caption, rect(r.x, r.y, r.w, capH), palette(s.textColor), { font: s.font, fontSize: Math.max(3.5, capH * 0.62) });
    return;
  }
  ctx.fillStyle = palette(s.fillColor); fillR(ctx, r);
  ctx.strokeStyle = palette(s.borderColor); ctx.lineWidth = Math.max(0.5, s.borderWidth);
  if (s.borderStyle === "dashed") ctx.setLineDash([4, 2]);
  strokeR(ctx, r); ctx.setLineDash([]);
  const i = inset(r, Math.max(4, r.w * 0.03), Math.max(4, r.h * 0.06));
  const logo = rect(i.x, i.y + i.h * 0.22, i.w * 0.2, i.h * 0.58);
  const logoImg = a.logoData ? env.image?.(a.logoData) ?? null : null;
  if (logoImg) drawImageIn(ctx, logoImg, aspectFit(logoImg, logo));
  else {
    ctx.fillStyle = palette(s.borderColor); fillR(ctx, inset(logo, 1, 1));
    drawLabel(ctx, "WATSON", inset(logo, 2, 2), s.fillColor === "clear" ? "rgba(255, 255, 255, 1)" : palette(s.fillColor), { font: "helveticaBold", fontScale: 0.2 });
  }
  const left = maxX(logo) + i.w * 0.035;
  const info = rect(left, i.y + i.h * 0.28, maxX(i) - left, i.h * 0.5);
  drawLabel(ctx, "VISUAL SIGNATURE APPEARANCE", rect(left, maxY(i) - i.h * 0.18, info.w, i.h * 0.14), palette(s.textColor), { font: s.font, fontSize: Math.max(6, s.fontSize), align: "left" });
  const lines = [a.name, a.email].filter((x) => x.trim());
  if (a.reason.trim()) lines.push(`Reason: ${a.reason}`);
  if (a.location.trim()) lines.push(`Location: ${a.location}`);
  if (a.includeTimestamp) lines.push(`Signed: ${(env.formatTimestamp ?? defaultTimestamp)(a.timestamp)}`);
  drawLabel(ctx, lines.join("\n"), info, palette(s.textColor), { font: s.font, fontSize: Math.max(5, s.fontSize * 0.82), align: "left" });
  drawLabel(ctx, "VISUAL SIGNATURE APPEARANCE — NOT CERTIFICATE-BACKED", rect(i.x, i.y, i.w, i.h * 0.15), palette(s.borderColor), { font: "helveticaBold", fontSize: Math.max(4.5, s.fontSize * 0.62) });
  if (a.freehandPaths.length) {
    const t = rect(maxX(r) - r.w * 0.31, r.y + r.h * 0.08, r.w * 0.25, r.h * 0.28);
    ctx.strokeStyle = palette(s.textColor); ctx.lineWidth = Math.max(1, Math.min(t.w, t.h) * 0.025);
    for (const p of a.freehandPaths) {
      if (p.points.length < 2) continue;
      ctx.beginPath();
      p.points.forEach((q, k) => { const [x, y] = denorm(q, t); if (k) ctx.lineTo(x, y); else ctx.moveTo(x, y); });
      ctx.stroke();
    }
  }
}

// Custom stamp geometry (CustomStampGeometry)
export interface StampFrames { content: Rect; logo: Rect | null; title: Rect; detail: Rect | null }
export function stampFrames(shape: CustomStampLayout["shape"], logoPosition: CustomStampLayout["logoPosition"], hasLogo: boolean, detailLineCount: number, r: Rect): StampFrames {
  const content = shape === "oval" ? inset(r, r.w * 0.15, r.h * 0.17)
    : shape === "doubleBorder" ? inset(r, Math.max(5, r.w * 0.08), Math.max(4, r.h * 0.13))
    : inset(r, Math.max(4, r.w * 0.05), Math.max(3, r.h * 0.09));
  let text = content;
  let logo: Rect | null = null;
  if (hasLogo) {
    if (logoPosition === "left") {
      const side = Math.min(content.h, content.w * 0.34);
      logo = rect(content.x, midY(content) - side / 2, side, side);
      const gap = content.w * 0.04;
      text = rect(content.x + side + gap, content.y, Math.max(1, content.w - side - gap), content.h);
    } else if (logoPosition === "top") {
      const h = content.h * 0.42;
      logo = rect(content.x, maxY(content) - h, content.w, h);
      text = rect(content.x, content.y, content.w, content.h - h - content.h * 0.03);
    } else logo = content;
  }
  if (detailLineCount <= 0) return { content, logo, title: text, detail: null };
  const dh = text.h * (detailLineCount > 1 ? 0.46 : 0.34);
  return { content, logo, detail: rect(text.x, text.y, text.w, dh), title: rect(text.x, text.y + dh, text.w, text.h - dh) };
}
export function stampDetailLines(d: ApprovalStampDetails): string[] {
  const lines: string[] = [];
  const second = d.project.trim();
  if (second) lines.push(second);
  const stamped = [d.name, d.date].map((x) => x.trim()).filter(Boolean);
  if (stamped.length) lines.push(stamped.join("  ·  "));
  return lines;
}
function stampBorderPath(ctx: Ctx, shape: CustomStampLayout["shape"], r: Rect) {
  ctx.beginPath();
  if (shape === "oval") ctx.ellipse(midX(r), midY(r), r.w / 2, r.h / 2, 0, 0, Math.PI * 2);
  else if (shape === "roundedRectangle") {
    const rad = Math.min(r.w, r.h) * 0.18;
    ctx.moveTo(r.x + rad, r.y);
    ctx.lineTo(maxX(r) - rad, r.y); ctx.arc(maxX(r) - rad, r.y + rad, rad, -Math.PI / 2, 0);
    ctx.lineTo(maxX(r), maxY(r) - rad); ctx.arc(maxX(r) - rad, maxY(r) - rad, rad, 0, Math.PI / 2);
    ctx.lineTo(r.x + rad, maxY(r)); ctx.arc(r.x + rad, maxY(r) - rad, rad, Math.PI / 2, Math.PI);
    ctx.lineTo(r.x, r.y + rad); ctx.arc(r.x + rad, r.y + rad, rad, Math.PI, Math.PI * 1.5);
    ctx.closePath();
  } else ctx.rect(r.x, r.y, r.w, r.h);
}

function drawStamp(ctx: Ctx, d: ApprovalStampDetails, r: Rect, env: RenderEnv) {
  const s = d.style;
  if (d.custom) {
    const layout = d.custom;
    const lw = Math.max(0, s.borderWidth);
    const outline = inset(r, lw / 2 + 0.5, lw / 2 + 0.5);
    if (outline.w <= 1 || outline.h <= 1) return;
    if (s.fillColor !== "clear") {
      ctx.save(); stampBorderPath(ctx, layout.shape, outline);
      ctx.fillStyle = withAlpha(paletteRGBA(s.fillColor), Math.min(1, Math.max(0, layout.fillOpacity)) * paletteRGBA(s.fillColor).a); ctx.fill(); ctx.restore();
    }
    const lines = stampDetailLines(d);
    const logoImg = layout.logoData ? env.image?.(layout.logoData) ?? null : null;
    const frames = stampFrames(layout.shape, layout.logoPosition, logoImg != null, lines.length, r);
    if (logoImg && frames.logo) {
      ctx.save();
      if (layout.logoPosition === "watermark") ctx.globalAlpha = 0.18;
      drawImageIn(ctx, logoImg, aspectFit(logoImg, frames.logo));
      ctx.restore();
    }
    if (s.borderColor !== "clear" && lw > 0) {
      ctx.save();
      ctx.strokeStyle = palette(s.borderColor); ctx.lineWidth = lw;
      const dashes = dashFor(s.borderStyle, lw);
      if (dashes) { ctx.lineCap = s.borderStyle === "dotted" ? "round" : "butt"; ctx.setLineDash(dashes); }
      stampBorderPath(ctx, layout.shape, outline); ctx.stroke();
      if (layout.shape === "doubleBorder") {
        const i = inset(outline, Math.max(2.5, lw * 1.8), Math.max(2.5, lw * 1.8));
        ctx.lineWidth = Math.max(0.5, lw * 0.5);
        ctx.beginPath(); ctx.rect(i.x, i.y, i.w, i.h); ctx.stroke();
      }
      ctx.restore();
    }
    const color = palette(s.textColor);
    const title = d.title.trim();
    if (title) drawLabel(ctx, title, frames.title, color, { font: s.font, fontSize: fittedFontSize(ctx, title, s.font, Math.max(6, s.fontSize * 2), frames.title) });
    if (frames.detail && lines.length) {
      const text = lines.join("\n");
      drawLabel(ctx, text, frames.detail, color, { font: s.font, fontSize: fittedFontSize(ctx, text, s.font, Math.max(4, s.fontSize * 0.85), frames.detail) });
    }
    return;
  }
  const color = palette(s.borderColor);
  ctx.fillStyle = palette(s.fillColor); fillR(ctx, r);
  ctx.strokeStyle = color; ctx.lineWidth = Math.max(0.8, s.borderWidth);
  if (s.borderStyle === "dashed") ctx.setLineDash([5, 2]);
  else { const dots = dashFor(s.borderStyle, s.borderWidth); if (dots) { ctx.lineCap = "round"; ctx.setLineDash(dots); } }
  strokeR(ctx, inset(r, 2, 2)); ctx.setLineDash([]);
  ctx.lineWidth = Math.max(0.6, s.borderWidth * 0.45); strokeR(ctx, inset(r, r.w * 0.07, r.h * 0.09));
  drawLabel(ctx, d.title, rect(r.x + r.w * 0.07, r.y + r.h * 0.54, r.w * 0.86, r.h * 0.32), palette(s.textColor), { font: s.font, fontSize: Math.max(8, s.fontSize * 1.7) });
  const lines = [d.name, d.date, d.project].filter(Boolean).join("\n");
  if (lines) drawLabel(ctx, lines, rect(r.x + r.w * 0.1, r.y + r.h * 0.12, r.w * 0.8, r.h * 0.33), palette(s.textColor), { font: s.font, fontSize: Math.max(5, s.fontSize * 0.82) });
}

function drawToolImage(ctx: Ctx, _asset: ToolChestImageAsset, r: Rect) {
  // Tool-chest pixels live in each user's own library, not in the document,
  // so the browser shows exactly what a Mac without that file shows.
  ctx.fillStyle = withAlpha(SYSTEM.gray!, 0.18); fillR(ctx, r);
  ctx.strokeStyle = css(SYSTEM.gray!); ctx.lineWidth = 1; strokeR(ctx, r);
  drawLabel(ctx, "Missing Tool Image", inset(r, 4, 4), css(SYSTEM.secondaryLabel!), { font: "helveticaBold", fontScale: 0.18 });
}

function drawHighlight(ctx: Ctx, s: HighlightStyle, r: Rect) {
  ctx.save();
  ctx.globalCompositeOperation = "multiply";
  ctx.fillStyle = withAlpha(paletteRGBA(s.color), Math.max(0.1, Math.min(0.9, s.opacity)));
  fillR(ctx, r);
  ctx.restore();
}

function signedArea(pts: readonly Vec2[]): number {
  let t = 0;
  for (let i = 0; i < pts.length; i++) { const a = pts[i]!, b = pts[(i + 1) % pts.length]!; t += a[0] * b[1] - b[0] * a[1]; }
  return t;
}

/** TakeoffDrawingAnnotation.cloudPath: one semicircular bump per ~2r, bulging outward. */
export function cloudPath(ctx: Ctx, vertices: readonly Vec2[], radius: number): boolean {
  if (vertices.length < 3 || !(radius > 0.5)) return false;
  const samples: Vec2[] = [];
  for (let i = 0; i < vertices.length; i++) {
    const a = vertices[i]!, b = vertices[(i + 1) % vertices.length]!;
    const len = Math.hypot(b[0] - a[0], b[1] - a[1]);
    if (!(len > 0.01)) continue;
    const bumps = Math.max(1, Math.round(len / (radius * 2)));
    for (let s = 0; s < bumps; s++) { const t = s / bumps; samples.push([a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t]); }
  }
  if (samples.length < 3) return false;
  const clockwise = signedArea(samples) < 0;
  ctx.beginPath();
  for (let i = 0; i < samples.length; i++) {
    const a = samples[i]!, b = samples[(i + 1) % samples.length]!;
    const span = Math.hypot(b[0] - a[0], b[1] - a[1]) / 2;
    if (!(span > 0.01)) continue;
    const angle = Math.atan2(b[1] - a[1], b[0] - a[0]);
    // CGPath "clockwise" in y-up space == canvas "counterclockwise" flag under the same transform.
    ctx.arc((a[0] + b[0]) / 2, (a[1] + b[1]) / 2, span, angle + Math.PI, angle, clockwise);
  }
  ctx.closePath();
  return true;
}

function drawCloud(ctx: Ctx, vertices: readonly Vec2[], s: CloudStyle) {
  if (s.fillColor !== "clear") { if (cloudPath(ctx, vertices, s.arcRadius)) { ctx.fillStyle = withAlpha(paletteRGBA(s.fillColor), s.fillOpacity); ctx.fill(); } }
  if (!cloudPath(ctx, vertices, s.arcRadius)) return;
  ctx.strokeStyle = palette(s.color); ctx.lineWidth = Math.max(0.3, s.lineWidth); ctx.lineJoin = "round";
  ctx.stroke();
}

function drawWorkArea(ctx: Ctx, vertices: readonly Vec2[], s: HatchStyle) {
  if (!vertices.length) return;
  path(ctx, vertices, true);
  ctx.fillStyle = withAlpha(paletteRGBA(s.fillColor), s.fillOpacity * paletteRGBA(s.fillColor).a); ctx.fill();
  ctx.save();
  path(ctx, vertices, true); ctx.clip();
  const b = pointsBounds(vertices);
  ctx.strokeStyle = palette(s.hatchColor); ctx.lineWidth = 1;
  const spacing = Math.max(3, s.hatchSpacing);
  ctx.beginPath();
  for (let x = b.x - b.h; x < maxX(b); x += spacing) { ctx.moveTo(x, b.y); ctx.lineTo(x + b.h, maxY(b)); }
  ctx.stroke(); ctx.restore();
  path(ctx, vertices, true); ctx.strokeStyle = palette(s.hatchColor); ctx.lineWidth = 1.5; ctx.stroke();
}

// Labeled shapes (ShapeMarkupRenderer)
export function shapeOutlineRect(s: ShapeMarkupStyle, r: Rect): Rect {
  if (s.shape !== "square" && s.shape !== "circle") return r;
  const side = Math.min(r.w, r.h);
  return rect(midX(r) - side / 2, midY(r) - side / 2, side, side);
}
const shapeFont = (s: ShapeMarkupStyle) => Math.min(72, Math.max(4, s.fontSize));
export function shapeLabelRect(s: ShapeMarkupStyle, r: Rect): Rect | null {
  const label = s.label.trim();
  if (s.labelPlacement === "hidden" || !label) return null;
  const o = shapeOutlineRect(s, r);
  const fs = shapeFont(s), lines = label.split("\n");
  const longest = Math.max(0, ...lines.map((l) => [...l].length));
  const w = Math.max(fs * 3, longest * fs * 0.64 + fs * 1.2), h = lines.length * fs * 1.2 + fs * 0.6;
  const gap = Math.max(2, s.lineWidth + 2);
  switch (s.labelPlacement) {
    case "inside": { const cy = s.shape === "triangle" ? o.y + o.h * 0.33 : midY(o); return rect(midX(o) - w / 2, cy - h / 2, w, h); }
    case "above": return rect(midX(o) - w / 2, maxY(o) + gap, w, h);
    case "below": return rect(midX(o) - w / 2, o.y - gap - h, w, h);
  }
  return null;
}
function shapePath(ctx: Ctx, kind: ShapeMarkupStyle["shape"], r: Rect) {
  ctx.beginPath();
  switch (kind) {
    case "rectangle": case "square": ctx.rect(r.x, r.y, r.w, r.h); break;
    case "circle": case "ellipse": ctx.ellipse(midX(r), midY(r), r.w / 2, r.h / 2, 0, 0, Math.PI * 2); break;
    case "triangle": ctx.moveTo(midX(r), maxY(r)); ctx.lineTo(maxX(r), r.y); ctx.lineTo(r.x, r.y); ctx.closePath(); break;
    case "diamond": ctx.moveTo(midX(r), maxY(r)); ctx.lineTo(maxX(r), midY(r)); ctx.lineTo(midX(r), r.y); ctx.lineTo(r.x, midY(r)); ctx.closePath(); break;
  }
}
function drawShape(ctx: Ctx, s: ShapeMarkupStyle, r: Rect) {
  const o = shapeOutlineRect(s, r);
  ctx.save();
  if (s.fillColor !== "clear" && s.fillOpacity > 0.001) { shapePath(ctx, s.shape, o); ctx.fillStyle = withAlpha(paletteRGBA(s.fillColor), Math.min(0.9, s.fillOpacity)); ctx.fill(); }
  shapePath(ctx, s.shape, o);
  ctx.strokeStyle = palette(s.strokeColor); ctx.lineWidth = Math.max(0.3, s.lineWidth); ctx.lineJoin = "miter";
  if (s.dashed) ctx.setLineDash([Math.max(4, s.lineWidth * 3), Math.max(3, s.lineWidth * 2)]);
  ctx.stroke();
  ctx.restore();
  const lr = shapeLabelRect(s, r);
  if (!lr) return;
  if (s.labelBackground) {
    ctx.fillStyle = withAlpha(SYSTEM.white!, 0.9); fillR(ctx, lr);
    ctx.strokeStyle = withAlpha(paletteRGBA(s.strokeColor), 0.8); ctx.lineWidth = 0.7; strokeR(ctx, lr);
  }
  const color = s.textColor === "ink" ? "rgba(0, 0, 0, 1)" : palette(s.textColor);
  drawLabel(ctx, s.label.trim(), lr, color, { font: "helveticaBold", fontSize: shapeFont(s), lineHeight: 1.2 });
}

// Quick measure
function drawEndTick(ctx: Ctx, p: Vec2, o: Vec2, color: string) {
  const dx = o[0] - p[0], dy = o[1] - p[1], len = Math.hypot(dx, dy);
  if (!(len > 0.01)) return;
  const nx = (-dy / len) * 5, ny = (dx / len) * 5;
  ctx.strokeStyle = color; ctx.lineWidth = 1.5;
  ctx.beginPath(); ctx.moveTo(p[0] - nx, p[1] - ny); ctx.lineTo(p[0] + nx, p[1] + ny); ctx.stroke();
}
export function normalizedDegrees(v: number): number { let x = v % 360; if (x <= -180) x += 360; if (x > 180) x -= 360; return x; }
const qmFont = (s: QuickMeasureStyle) => Math.min(72, Math.max(4, s.fontSize));
export function quickMeasureLabelRect(text: string, vertices: readonly Vec2[], closed: boolean, fontSize: number): Rect | null {
  const a = labelAnchor(vertices, closed && vertices.length >= 3);
  if (!a) return null;
  const size = labelSize(text, fontSize);
  return rect(a[0] - size.w / 2, a[1] - size.h / 2, size.w, size.h);
}
/** A plain straight Line (quickMeasure with `lineWidth`): solid stroke; length label only on a scaled sheet. */
function drawPlainLine(ctx: Ctx, m: Markup & { kind: "quickMeasure" }, scale: PageScale | null | undefined) {
  const s = m.style, vertices = quickMeasureVertices(m);
  if (vertices.length < 2) return;
  const color = palette(s.color);
  ctx.save();
  path(ctx, vertices, false);
  ctx.lineCap = "round"; ctx.lineJoin = "round"; ctx.lineWidth = Math.max(0.3, s.lineWidth ?? 2); ctx.strokeStyle = color; ctx.setLineDash([]); ctx.stroke();
  ctx.restore();
  if (!scale) return;
  const label = quickMeasureMarkupLabel(m, scale);
  const fs = qmFont(s);
  const r = label ? quickMeasureLabelRect(label, vertices, false, fs) : null;
  if (!label || !r) return;
  ctx.save();
  const turn = normalizedDegrees(s.labelRotationDegrees - m.rotationDegrees);
  if (turn) rotateAbout(ctx, midX(r), midY(r), turn);
  ctx.fillStyle = withAlpha(SYSTEM.white!, 0.92); fillR(ctx, r);
  ctx.strokeStyle = color; ctx.lineWidth = 0.9; strokeR(ctx, r);
  const pad = Math.max(2, fs * 0.5);
  drawLabel(ctx, label, inset(r, pad, pad * 0.8), "rgba(0, 0, 0, 1)", { font: "helveticaBold", fontSize: fs });
  ctx.restore();
}
function drawQuickMeasure(ctx: Ctx, m: Markup & { kind: "quickMeasure" }, scale: PageScale | null | undefined) {
  if (m.style.lineWidth != null && !m.style.closed) return drawPlainLine(ctx, m, scale);
  const s = m.style, vertices = quickMeasureVertices(m);
  const label = quickMeasureMarkupLabel(m, scale) ?? "";
  const warning = !scale;
  const rgba = paletteRGBA(s.color), color = css(rgba);
  if (!vertices.length) return;
  const ring = s.closed && vertices.length >= 3;
  ctx.save();
  if (ring) { path(ctx, vertices, true); ctx.fillStyle = withAlpha(rgba, 0.12); ctx.fill(); }
  path(ctx, vertices, ring);
  ctx.lineCap = "butt"; ctx.lineWidth = 1.5; ctx.strokeStyle = color; ctx.setLineDash([6, 3]); ctx.stroke(); ctx.setLineDash([]);
  if (!ring && vertices.length >= 2) {
    drawEndTick(ctx, vertices[0]!, vertices[1]!, color);
    drawEndTick(ctx, vertices[vertices.length - 1]!, vertices[vertices.length - 2]!, color);
  }
  ctx.fillStyle = color;
  for (const [x, y] of vertices) fillEllipse(ctx, x, y, 1.6);
  ctx.restore();
  const fs = qmFont(s);
  const r = quickMeasureLabelRect(label, vertices, s.closed, fs);
  if (!r) return;
  ctx.save();
  const turn = normalizedDegrees(s.labelRotationDegrees - m.rotationDegrees);
  if (turn) rotateAbout(ctx, midX(r), midY(r), turn);
  ctx.fillStyle = withAlpha(SYSTEM.white!, 0.92); fillR(ctx, r);
  ctx.strokeStyle = warning ? css(SYSTEM.red!) : color; ctx.lineWidth = warning ? 1.2 : 0.9; strokeR(ctx, r);
  const pad = Math.max(2, fs * 0.5);
  drawLabel(ctx, label, inset(r, pad, pad * 0.8), warning ? css(SYSTEM.red!) : "rgba(0, 0, 0, 1)", { font: "helveticaBold", fontSize: fs });
  ctx.restore();
}

/** Ring of a box (default vertices for work areas and clouds). */
export function ringOf([x, y, w, h]: readonly number[]): Vec2[] { return [[x!, y!], [x! + w!, y!], [x! + w!, y! + h!], [x!, y! + h!]]; }

/** Draws one markup, turned about the centre of its box like the Mac. */
export function drawMarkup(ctx: Ctx, m: Markup, scale: PageScale | null | undefined, env: RenderEnv = {}): void {
  const r = fromBox(m.bounds);
  ctx.save();
  ctx.lineJoin = "round"; ctx.lineCap = "round";
  if (m.rotationDegrees) rotateAbout(ctx, midX(r), midY(r), m.rotationDegrees);
  switch (m.kind) {
    case "note": drawTextBox(ctx, m.style, r); break;
    case "signature": drawSignature(ctx, m.style, r, env); break;
    case "mot": drawMOT(ctx, m.style, r); break;
    case "workArea": drawWorkArea(ctx, m.vertices ?? ringOf(m.bounds), m.style); break;
    case "approvalStamp": drawStamp(ctx, m.style, r, env); break;
    case "image": drawToolImage(ctx, m.style, r); break;
    case "highlight": drawHighlight(ctx, m.style, r); break;
    case "cloud": drawCloud(ctx, m.vertices ?? ringOf(m.bounds), m.style); break;
    case "shape": drawShape(ctx, m.style, r); break;
    case "quickMeasure": drawQuickMeasure(ctx, m, scale); break;
  }
  ctx.restore();
}

/** Measurements first, then markups — the Mac's stacking order. Hidden layers are skipped. */
export function drawPage(
  ctx: Ctx,
  items: { measurements: readonly Measurement[]; markups: readonly Markup[] },
  pageId: string,
  scale: PageScale | null | undefined,
  visibleLayers: readonly string[] | null,
  env: RenderEnv = {},
  skip?: ReadonlySet<string>,
): void {
  const shown = (layer: string) => !visibleLayers || visibleLayers.includes(layer);
  for (const m of items.measurements) if (m.pageId === pageId && shown(m.layer) && !skip?.has(m.id)) drawMeasurement(ctx, m, scale);
  for (const m of items.markups) if (m.pageId === pageId && shown(m.layer) && !skip?.has(m.id)) drawMarkup(ctx, m, scale, env);
}

export type { RGBA };
